import { useMemo, useState } from "react";
import { useModals } from "../components/common/ModalsProvider.js";
import { FiscalYearSelector } from "../components/fiscal/FiscalYearSelector.js";
import {
  FiscalCard,
  FiscalShell,
  LoadingSkeleton,
  fiscalInputClass,
} from "../components/fiscal/Shell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useFiscalSelection } from "../hooks/useFiscalSelection.js";
import { useT } from "../i18n/index.js";
import { deltaColorClass, formatMoney, formatMoneySigned } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { useAbaPendente } from "../lib/open-file.js";

const STATUS_TONE: Record<string, string> = {
  draft: "bg-neutral-700/40 text-neutral-300",
  declared: "bg-amber-500/15 text-amber-300",
  paid: "bg-lime/15 text-lime",
  cancelled: "bg-red-500/15 text-red-300",
};

export default function FiscalTva() {
  const { user } = useAuth();
  const modals = useModals();
  const { companyId, fiscalYearId, setCompanyId, setFiscalYearId } = useFiscalSelection();

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const t = useT();

  const MONTH_LABELS = [
    t("fiscalTva.monthJanuary"),
    t("fiscalTva.monthFebruary"),
    t("fiscalTva.monthMarch"),
    t("fiscalTva.monthApril"),
    t("fiscalTva.monthMay"),
    t("fiscalTva.monthJune"),
    t("fiscalTva.monthJuly"),
    t("fiscalTva.monthAugust"),
    t("fiscalTva.monthSeptember"),
    t("fiscalTva.monthOctober"),
    t("fiscalTva.monthNovember"),
    t("fiscalTva.monthDecember"),
  ];

  const STATUS_LABELS: Record<string, string> = {
    draft: t("fiscalTva.statusDraft"),
    declared: t("fiscalTva.statusDeclared"),
    paid: t("fiscalTva.statusPaid"),
    cancelled: t("fiscalTva.statusCancelled"),
  };

  const isAdmin =
    user?.isSuperAdmin ||
    user?.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin");

  const listQuery = trpc.tvaDeclarations.list.useQuery(
    { companyId: companyId ?? 0, year },
    { enabled: companyId !== null },
  );

  const utils = trpc.useUtils();
  const computeMutation = trpc.tvaDeclarations.compute.useMutation({
    onSuccess: () => utils.tvaDeclarations.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("fiscalTva.error"),
        message: `${t("fiscalTva.error")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const declareMutation = trpc.tvaDeclarations.declare.useMutation({
    onSuccess: () => utils.tvaDeclarations.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("fiscalTva.error"),
        message: `${t("fiscalTva.error")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const markPaidMutation = trpc.tvaDeclarations.markPaid.useMutation({
    onSuccess: () => utils.tvaDeclarations.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("fiscalTva.error"),
        message: `${t("fiscalTva.error")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const reopenMutation = trpc.tvaDeclarations.reopen.useMutation({
    onSuccess: () => utils.tvaDeclarations.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("fiscalTva.error"),
        message: `${t("fiscalTva.error")}: ${e.message}`,
        tone: "danger",
      }),
  });

  const abaExport = useAbaPendente();
  const generateExportMutation = trpc.exportComptable.generateMonthlyReport.useMutation({
    onSuccess: (result) => {
      utils.exportComptable.invalidate();
      // Abre o arquivo na aba aberta no clique (download)
      abaExport.ir(result.downloadUrl);
    },
    onError: (e) => {
      abaExport.fechar();
      void modals.alert({
        title: t("fiscalTva.error"),
        message: `${t("fiscalTva.errorGeneratingExport")}: ${e.message}`,
        tone: "danger",
      });
    },
  });

  const exportsListQuery = trpc.exportComptable.listReports.useQuery(
    { companyId: companyId ?? 0, year },
    { enabled: companyId !== null },
  );
  const exportsByMonthFormat = useMemo(() => {
    const map = new Map<string, NonNullable<typeof exportsListQuery.data>[number]>();
    for (const e of exportsListQuery.data ?? []) {
      map.set(`${e.month}-${e.format}`, e);
    }
    return map;
  }, [exportsListQuery.data]);

  const byMonth = useMemo(() => {
    const map = new Map<number, NonNullable<typeof listQuery.data>[number]>();
    for (const d of listQuery.data ?? []) {
      map.set(d.month, d);
    }
    return map;
  }, [listQuery.data]);

  const selected = selectedMonth !== null ? byMonth.get(selectedMonth) : null;

  const toolbar = (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <FiscalYearSelector
        companyId={companyId}
        fiscalYearId={fiscalYearId}
        onChangeCompany={setCompanyId}
        onChangeFiscalYear={(fy) => setFiscalYearId(fy?.id ?? null)}
      />
      <div className="flex items-center gap-2">
        <label className="text-xs uppercase tracking-wider text-neutral-500">
          {t("fiscalTva.tvaYearLabel")}
        </label>
        <select
          value={year}
          onChange={(e) => {
            setYear(Number(e.target.value));
            setSelectedMonth(null);
          }}
          className={fiscalInputClass + " w-24"}
        >
          {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <FiscalShell subtitle={t("fiscalTva.subtitle")} toolbar={toolbar}>
      {companyId === null && (
        <FiscalCard>
          <p className="text-sm text-neutral-400">{t("fiscalTva.selectCompany")}</p>
        </FiscalCard>
      )}

      {companyId !== null && listQuery.isLoading && (
        <FiscalCard>
          <LoadingSkeleton rows={6} />
        </FiscalCard>
      )}

      {companyId !== null && !listQuery.isLoading && (
        <FiscalCard
          title={`${t("fiscalTva.yearPre")} ${year}`}
          subtitle={t("fiscalTva.tableSubtitle")}
          padded={false}
        >
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-neutral-500">
              <tr className="border-b border-neutral-800">
                <th className="text-left px-5 py-2 w-32">{t("fiscalTva.colMonth")}</th>
                <th className="text-left px-3 py-2 w-24">{t("fiscalTva.status")}</th>
                <th className="text-right px-3 py-2 font-mono">{t("fiscalTva.colCaHt")}</th>
                <th className="text-right px-3 py-2 font-mono">{t("fiscalTva.colAchatsHt")}</th>
                <th className="text-right px-3 py-2 font-mono">{t("fiscalTva.tvaCollectee")}</th>
                <th className="text-right px-3 py-2 font-mono">{t("fiscalTva.tvaDeductible")}</th>
                <th className="text-right px-3 py-2 font-mono">{t("fiscalTva.colToPayCredit")}</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-900">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                const decl = byMonth.get(m);
                const isSelected = selectedMonth === m;
                return (
                  <tr
                    key={m}
                    onClick={() => setSelectedMonth(isSelected ? null : m)}
                    className={`cursor-pointer hover:bg-neutral-800/40 ${
                      isSelected ? "bg-lime/5" : ""
                    }`}
                  >
                    <td className="px-5 py-2 text-neutral-100">{MONTH_LABELS[m - 1]}</td>
                    <td className="px-3 py-2">
                      {decl ? (
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_TONE[decl.status]}`}
                        >
                          {STATUS_LABELS[decl.status] ?? decl.status}
                        </span>
                      ) : (
                        <span className="text-xs text-neutral-600">
                          {t("fiscalTva.emptyMonth")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-300">
                      {decl ? formatMoney(decl.caHtCollecte) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-300">
                      {decl ? formatMoney(decl.achatsHtDeductible) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-300">
                      {decl ? formatMoney(decl.tvaCollectee) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-300">
                      {decl ? formatMoney(decl.tvaDeductible) : "—"}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${
                        decl && Number(decl.tvaAPayer) > 0
                          ? "text-amber-300"
                          : decl && Number(decl.creditTvaReportable) > 0
                            ? "text-lime"
                            : "text-neutral-400"
                      }`}
                    >
                      {decl
                        ? Number(decl.tvaAPayer) > 0
                          ? formatMoney(decl.tvaAPayer)
                          : Number(decl.creditTvaReportable) > 0
                            ? `+${formatMoney(decl.creditTvaReportable)} ${t("fiscalTva.creditSuf")}`
                            : "0 €"
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!companyId) return;
                          computeMutation.mutate({
                            companyId,
                            year,
                            month: m,
                          });
                        }}
                        disabled={
                          computeMutation.isPending ||
                          decl?.status === "declared" ||
                          decl?.status === "paid"
                        }
                        className="rounded-md border border-lime/40 hover:border-lime disabled:opacity-30 text-lime px-2 py-0.5 text-xs"
                      >
                        {t("fiscalTva.computeBtn")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </FiscalCard>
      )}

      {selected && companyId !== null && (
        <FiscalCard
          title={`${t("fiscalTva.detailPre")} ${MONTH_LABELS[selected.month - 1]} ${selected.year}`}
          subtitle={`${t("fiscalTva.status")}: ${STATUS_LABELS[selected.status] ?? selected.status} · ${t("fiscalTva.computedAtPre")} ${
            selected.lastComputedAt
              ? new Date(selected.lastComputedAt).toLocaleString("fr-FR")
              : "—"
          }`}
          action={
            <div className="flex gap-2">
              {selected.status === "draft" && (
                <button
                  onClick={async () => {
                    const ref = await modals.promptReason({
                      title: t("fiscalTva.declareTitle"),
                      message: t("fiscalTva.declareMessage"),
                      placeholder: t("fiscalTva.declarePlaceholder"),
                      confirmLabel: t("fiscalTva.declareConfirm"),
                      tone: "warning",
                      required: false,
                    });
                    if (ref === null) return; // cancelado
                    declareMutation.mutate({
                      companyId,
                      year: selected.year,
                      month: selected.month,
                      ...(ref ? { declarationReference: ref } : {}),
                    });
                  }}
                  disabled={declareMutation.isPending}
                  className="rounded-md border border-amber-500/40 hover:border-amber-500 text-amber-300 px-3 py-1 text-xs"
                >
                  {t("fiscalTva.declareConfirm")}
                </button>
              )}
              {selected.status === "declared" && (
                <button
                  onClick={async () => {
                    const amount = await modals.promptNumber({
                      title: t("fiscalTva.markPaidTitle"),
                      message: `${t("fiscalTva.suggestedPre")}: ${selected.tvaAPayer} €`,
                      defaultValue: Number(selected.tvaAPayer),
                      min: 0,
                      step: 0.01,
                      suffix: "€",
                      confirmLabel: t("fiscalTva.markPaidConfirm"),
                      required: true,
                    });
                    if (amount === null) return;
                    markPaidMutation.mutate({
                      companyId,
                      year: selected.year,
                      month: selected.month,
                      paidAmount: amount,
                    });
                  }}
                  disabled={markPaidMutation.isPending}
                  className="rounded-md border border-lime/40 hover:border-lime text-lime px-3 py-1 text-xs"
                >
                  {t("fiscalTva.markPaidBtn")}
                </button>
              )}
              {(selected.status === "declared" || selected.status === "paid") && isAdmin && (
                <button
                  onClick={async () => {
                    const reason = await modals.promptReason({
                      title: t("fiscalTva.reopenTitle"),
                      message: t("fiscalTva.reopenMessage"),
                      placeholder: t("fiscalTva.reopenPlaceholder"),
                      confirmLabel: t("fiscalTva.reopenConfirm"),
                      tone: "danger",
                      required: true,
                    });
                    if (!reason) return;
                    reopenMutation.mutate({
                      companyId,
                      year: selected.year,
                      month: selected.month,
                      reason,
                    });
                  }}
                  disabled={reopenMutation.isPending}
                  className="rounded-md border border-red-500/40 hover:border-red-500 text-red-300 px-3 py-1 text-xs"
                >
                  {t("fiscalTva.reopenConfirm")}
                </button>
              )}
              <span className="w-px h-5 bg-neutral-700" />
              <button
                onClick={() => {
                  abaExport.abrir();
                  generateExportMutation.mutate({
                    companyId,
                    year: selected.year,
                    month: selected.month,
                    format: "pdf",
                  });
                }}
                disabled={generateExportMutation.isPending}
                title={
                  exportsByMonthFormat.has(`${selected.month}-pdf`)
                    ? `${t("fiscalTva.regeneratePre")} (${t("fiscalTva.lastPre")}: ${
                        exportsByMonthFormat.get(`${selected.month}-pdf`)?.generatedAt
                          ? new Date(
                              exportsByMonthFormat.get(`${selected.month}-pdf`)!.generatedAt,
                            ).toLocaleString("fr-FR")
                          : "—"
                      })`
                    : t("fiscalTva.generatePdfTitle")
                }
                className="rounded-md border border-sky-500/40 hover:border-sky-500 text-sky-300 px-3 py-1 text-xs disabled:opacity-50"
              >
                {t("fiscalTva.exportPdfBtn")}
              </button>
              <button
                onClick={() => {
                  abaExport.abrir();
                  generateExportMutation.mutate({
                    companyId,
                    year: selected.year,
                    month: selected.month,
                    format: "csv",
                  });
                }}
                disabled={generateExportMutation.isPending}
                title={t("fiscalTva.generateCsvTitle")}
                className="rounded-md border border-sky-500/40 hover:border-sky-500 text-sky-300 px-3 py-1 text-xs disabled:opacity-50"
              >
                {t("fiscalTva.exportCsvBtn")}
              </button>
            </div>
          }
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <DetailItem
              label={t("fiscalTva.labelCaHtCollected")}
              value={formatMoney(selected.caHtCollecte)}
              sub={t("fiscalTva.subCaHtCollected")}
            />
            <DetailItem
              label={t("fiscalTva.labelAchatsHtDeductible")}
              value={formatMoney(selected.achatsHtDeductible)}
              sub={t("fiscalTva.subAchatsHtDeductible")}
            />
            <DetailItem
              label={t("fiscalTva.tvaCollectee")}
              value={formatMoney(selected.tvaCollectee)}
              sub={`${t("fiscalTva.suggestedPre")}: ${formatMoney(selected.tvaCollecteeSuggested)}`}
              diff={Number(selected.tvaCollectee) - Number(selected.tvaCollecteeSuggested)}
            />
            <DetailItem
              label={t("fiscalTva.tvaDeductible")}
              value={formatMoney(selected.tvaDeductible)}
              sub={`${t("fiscalTva.suggestedPre")}: ${formatMoney(selected.tvaDeductibleSuggested)}`}
              diff={Number(selected.tvaDeductible) - Number(selected.tvaDeductibleSuggested)}
            />
            <DetailItem
              label={t("fiscalTva.labelTvaAPayer")}
              value={formatMoney(selected.tvaAPayer)}
              sub={t("fiscalTva.subTvaAPayer")}
              tone={Number(selected.tvaAPayer) > 5000 ? "amber" : "neutral"}
            />
            <DetailItem
              label={t("fiscalTva.labelCreditReportable")}
              value={formatMoney(selected.creditTvaReportable)}
              sub={t("fiscalTva.subCreditReportable")}
              tone={Number(selected.creditTvaReportable) > 0 ? "positive" : "neutral"}
            />
            {selected.declarationReference && (
              <DetailItem
                label={t("fiscalTva.labelDgfipReference")}
                value={selected.declarationReference}
                sub={
                  selected.declaredAt
                    ? `${t("fiscalTva.declaredAtPre")} ${new Date(selected.declaredAt).toLocaleDateString("fr-FR")}`
                    : ""
                }
              />
            )}
            {selected.paidAmount && (
              <DetailItem
                label={t("fiscalTva.labelPaidAmount")}
                value={formatMoney(selected.paidAmount)}
                sub={
                  selected.paidAt
                    ? `${t("fiscalTva.paidAtPre")} ${new Date(selected.paidAt).toLocaleDateString("fr-FR")}`
                    : ""
                }
              />
            )}
          </div>
          {selected.overrideAt && (
            <p className="text-xs text-amber-400 mt-3">
              ⚠ {t("fiscalTva.overrideAppliedPre")}{" "}
              {new Date(selected.overrideAt).toLocaleString("fr-FR")} — {selected.overrideReason}
            </p>
          )}
          {Array.isArray(selected.warnings) && selected.warnings.length > 0 && (
            <ul className="text-xs text-neutral-400 mt-3 space-y-1">
              {(selected.warnings as string[]).map((w, i) => (
                <li key={i}>⚠ {w}</li>
              ))}
            </ul>
          )}
        </FiscalCard>
      )}
    </FiscalShell>
  );
}

function DetailItem({
  label,
  value,
  sub,
  diff,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  diff?: number;
  tone?: "amber" | "positive" | "neutral";
}) {
  const t = useT();
  const toneCls =
    tone === "amber" ? "text-amber-300" : tone === "positive" ? "text-lime" : "text-neutral-100";
  return (
    <div className="bg-neutral-800/30 rounded-md px-3 py-2">
      <div className="text-xs uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`font-mono tabular-nums text-lg ${toneCls}`}>{value}</div>
      {sub && <div className="text-[10px] text-neutral-500 mt-0.5">{sub}</div>}
      {diff !== undefined && diff !== 0 && Number.isFinite(diff) && (
        <div className={`text-[10px] mt-0.5 font-mono ${deltaColorClass(diff)}`}>
          {formatMoneySigned(diff)} ({t("fiscalTva.overrideSuf")})
        </div>
      )}
    </div>
  );
}
